/**
 * ============================================================================
 * UTILIDADES DE FECHA - ZONA HORARIA CIUDAD DE MÉXICO
 * ============================================================================
 * ARQUITECTURA:
 * - MySQL está configurado con time_zone = 'America/Mexico_City' en cada conexión
 * - Las fechas en la BD están en hora de México
 * - El frontend interpreta las fechas como hora de México
 * ============================================================================
 */

const TIMEZONE = "America/Mexico_City";
const LOCALE = "es-MX";

/**
 * Parsear fecha del servidor MySQL.
 * MySQL devuelve "2026-03-06 10:15:00" que YA ESTÁ en hora de México.
 * Esta función lo convierte a un objeto Date interpretándolo como hora de México (UTC-6).
 *
 * @param {string} dateString - Fecha en formato del servidor (hora de México)
 * @returns {Date|null} - Objeto Date o null si inválido
 */
const parseServerDate = (dateString) => {
  if (!dateString) return null;

  // Si ya tiene zona horaria (Z o offset), usarlo directamente
  if (/[Z]|[+-]\d{2}:?\d{2}$/.test(dateString)) {
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? null : date;
  }

  // Para fechas tipo "2026-03-06 10:15:00" de MySQL:
  // Interpretarlas como hora de México (UTC-6 estándar / UTC-5 horario de verano)
  // Usamos -06:00 que es el offset estándar de México Central

  // Normalizar formato: reemplazar espacio por T
  let normalized = dateString.trim().replace(" ", "T");

  // Si no tiene hora, agregar mediodía
  if (!normalized.includes("T")) {
    normalized += "T12:00:00";
  }

  // Agregar offset de México (-06:00) para indicar que la hora ya es de México
  const date = new Date(normalized + "-06:00");
  return isNaN(date.getTime()) ? null : date;
};

/**
 * Formatear fecha completa con hora
 * Ejemplo: "6 mar 2026, 10:15 a.m."
 */
export const formatDateTime = (dateString) => {
  if (!dateString) return "Sin fecha";
  try {
    const date = parseServerDate(dateString);
    if (!date) return "Fecha inválida";
    return date.toLocaleString(LOCALE, {
      timeZone: TIMEZONE,
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (e) {
    console.error("Error formateando fecha:", e);
    return "Fecha inválida";
  }
};

/**
 * Formatear solo fecha (sin hora)
 * Ejemplo: "6 mar 2026"
 */
export const formatDate = (dateString) => {
  if (!dateString) return "Sin fecha";
  try {
    const date = parseServerDate(dateString);
    if (!date) return "Fecha inválida";
    return date.toLocaleString(LOCALE, {
      timeZone: TIMEZONE,
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch (e) {
    console.error("Error formateando fecha:", e);
    return "Fecha inválida";
  }
};

/**
 * Formatear solo hora
 * Ejemplo: "10:15 a.m."
 */
export const formatTime = (dateString) => {
  if (!dateString) return "Sin hora";
  try {
    const date = parseServerDate(dateString);
    if (!date) return "Hora inválida";
    return date.toLocaleString(LOCALE, {
      timeZone: TIMEZONE,
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (e) {
    console.error("Error formateando hora:", e);
    return "Hora inválida";
  }
};

/**
 * Formatear fecha larga
 * Ejemplo: "Viernes, 6 de marzo de 2026"
 */
export const formatDateLong = (dateString) => {
  if (!dateString) return "Sin fecha";
  try {
    const date = parseServerDate(dateString);
    if (!date) return "Fecha inválida";
    return date.toLocaleString(LOCALE, {
      timeZone: TIMEZONE,
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch (e) {
    console.error("Error formateando fecha:", e);
    return "Fecha inválida";
  }
};

/**
 * Formatear tiempo relativo
 * Ejemplo: "Hace 5 minutos", "Hace 2 horas", "Ayer"
 */
export const formatRelativeTime = (dateString) => {
  if (!dateString) return "";
  try {
    const date = parseServerDate(dateString);
    if (!date) return "";

    const now = new Date();
    const diffMs = now - date;
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMinutes < 1) return "Ahora mismo";
    if (diffMinutes < 60)
      return `Hace ${diffMinutes} minuto${diffMinutes !== 1 ? "s" : ""}`;
    if (diffHours < 24)
      return `Hace ${diffHours} hora${diffHours !== 1 ? "s" : ""}`;
    if (diffDays === 1) return "Ayer";
    if (diffDays < 7) return `Hace ${diffDays} días`;
    if (diffDays < 30)
      return `Hace ${Math.floor(diffDays / 7)} semana${Math.floor(diffDays / 7) !== 1 ? "s" : ""}`;

    // Más de un mes, mostrar fecha
    return formatDate(dateString);
  } catch (e) {
    console.error("Error formateando tiempo relativo:", e);
    return "";
  }
};

/**
 * Obtener fecha actual en zona horaria de México
 */
export const getNowMexico = () => {
  return new Date().toLocaleString(LOCALE, {
    timeZone: TIMEZONE,
  });
};

/**
 * Obtener fecha ISO ajustada a México (para inputs type="date")
 */
export const getDateInputValue = (dateString) => {
  if (!dateString) return "";
  try {
    const date = parseServerDate(dateString);
    if (!date) return "";
    // Convertir a formato YYYY-MM-DD
    const options = { timeZone: TIMEZONE };
    const year = date.toLocaleString(LOCALE, { ...options, year: "numeric" });
    const month = date.toLocaleString(LOCALE, { ...options, month: "2-digit" });
    const day = date.toLocaleString(LOCALE, { ...options, day: "2-digit" });
    return `${year}-${month}-${day}`;
  } catch (e) {
    return "";
  }
};

/**
 * Formatear fecha para tabla/lista corta
 * Ejemplo: "6/mar/2026"
 */
export const formatDateShort = (dateString) => {
  if (!dateString) return "-";
  try {
    const date = parseServerDate(dateString);
    if (!date) return "-";
    return date.toLocaleString(LOCALE, {
      timeZone: TIMEZONE,
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch (e) {
    return "-";
  }
};

/**
 * Formatear fecha y hora para sesiones/actividad
 * Ejemplo: "6 mar 2026, 02:39 p.m."
 */
export const formatSessionTime = (dateString) => {
  return formatDateTime(dateString);
};

/**
 * Obtener fecha actual para inputs type="date" (YYYY-MM-DD)
 * Usa zona horaria de México para evitar desfase de días
 */
export const getCurrentDateForInput = () => {
  const now = new Date();
  const options = { timeZone: TIMEZONE };
  const year = now.toLocaleString(LOCALE, { ...options, year: "numeric" });
  const month = now.toLocaleString(LOCALE, { ...options, month: "2-digit" });
  const day = now.toLocaleString(LOCALE, { ...options, day: "2-digit" });
  return `${year}-${month}-${day}`;
};

/**
 * Obtener fecha/hora actual en ISO para guardar en DB
 * Ajustado a zona horaria de México
 */
export const getCurrentISOString = () => {
  return new Date().toISOString();
};

/**
 * Obtener año actual en zona horaria de México
 */
export const getCurrentYear = () => {
  const now = new Date();
  return parseInt(
    now.toLocaleString(LOCALE, { timeZone: TIMEZONE, year: "numeric" }),
  );
};

/**
 * Obtener mes actual (1-12) en zona horaria de México
 */
export const getCurrentMonth = () => {
  const now = new Date();
  return parseInt(
    now.toLocaleString(LOCALE, { timeZone: TIMEZONE, month: "numeric" }),
  );
};

/**
 * Formatear solo el mes (corto)
 * Ejemplo: "mar"
 */
export const formatMonth = (dateString) => {
  if (!dateString) return "";
  try {
    const date = parseServerDate(dateString);
    if (!date) return "";
    return date.toLocaleString(LOCALE, {
      timeZone: TIMEZONE,
      month: "short",
    });
  } catch (e) {
    return "";
  }
};

/**
 * Formatear día del mes
 * Ejemplo: "6"
 */
export const formatDay = (dateString) => {
  if (!dateString) return "";
  try {
    const date = parseServerDate(dateString);
    if (!date) return "";
    return date.toLocaleString(LOCALE, {
      timeZone: TIMEZONE,
      day: "numeric",
    });
  } catch (e) {
    return "";
  }
};

/**
 * Formatear día de la semana
 * Ejemplo: "Viernes"
 */
export const formatWeekday = (dateString) => {
  if (!dateString) return "";
  try {
    const date = parseServerDate(dateString);
    if (!date) return "";
    return date.toLocaleString(LOCALE, {
      timeZone: TIMEZONE,
      weekday: "long",
    });
  } catch (e) {
    return "";
  }
};

/**
 * Parsear fecha local sin problemas de timezone
 * Útil para fechas que vienen como "2026-03-06"
 * Ahora delegamos a parseServerDate para consistencia
 */
export const parseFechaLocal = (dateString) => {
  return parseServerDate(dateString);
};

// Exportar constantes
export const DATE_TIMEZONE = TIMEZONE;
export const DATE_LOCALE = LOCALE;

// Exportación por defecto
const dateUtils = {
  formatDateTime,
  formatDate,
  formatTime,
  formatDateLong,
  formatRelativeTime,
  getNowMexico,
  getDateInputValue,
  formatDateShort,
  formatSessionTime,
  getCurrentDateForInput,
  getCurrentISOString,
  getCurrentYear,
  getCurrentMonth,
  formatMonth,
  formatDay,
  formatWeekday,
  parseFechaLocal,
  TIMEZONE,
  LOCALE,
};

export default dateUtils;
